import type {
  ShippingZoneCreateInput,
  ShippingZoneUpdateInput,
} from "@/modules/shipping/domain/contracts";
import { Button, FormInput, FormRoot, RHF, zodResolver } from "@devhop/ui";
import type { Resolver, UseFormReturn } from "react-hook-form";
import { z } from "zod";

const ZoneFormSchema = z.object({
  code: z.string().min(1, "ຕ້ອງໃສ່ລະຫັດໂຊນ"),
  nameLo: z.string().optional(),
  nameTh: z.string().optional(),
  nameEn: z.string().optional(),
  sortOrder: z.coerce.number().int().min(0).default(0),
});

export type ZoneFormValues = z.output<typeof ZoneFormSchema>;

const DEV_FILL: ZoneFormValues = {
  code: "VTE",
  nameLo: "ວຽງຈັນ",
  nameTh: "เวียงจันทน์",
  nameEn: "Vientiane",
  sortOrder: 0,
};

type ZoneFormProps = {
  initialValues?: Partial<ZoneFormValues>;
  onSubmit: (values: ShippingZoneCreateInput | ShippingZoneUpdateInput) => void;
  onCancel?: () => void;
  submitting?: boolean;
  onDevFill?: () => void;
};

export function ZoneForm({
  initialValues,
  onSubmit,
  onCancel,
  submitting,
  onDevFill,
}: ZoneFormProps) {
  const methods = RHF.useForm<ZoneFormValues>({
    resolver: zodResolver(ZoneFormSchema) as Resolver<ZoneFormValues>,
    defaultValues: {
      code: "",
      nameLo: "",
      nameTh: "",
      nameEn: "",
      sortOrder: 0,
      ...initialValues,
    },
  });

  const fillDev = () => {
    methods.reset(DEV_FILL);
  };

  return (
    <FormRoot<ZoneFormValues>
      methods={methods as UseFormReturn<ZoneFormValues>}
      onSubmit={(vals) =>
        onSubmit({
          code: vals.code,
          name: { lo: vals.nameLo, th: vals.nameTh, en: vals.nameEn },
          sortOrder: vals.sortOrder,
        })
      }
    >
      <div className="space-y-4" data-tourid="shipping-zone-form">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h3 className="font-medium">ຂໍ້ມູນໂຊນ</h3>
          {onDevFill && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={fillDev}
            >
              ເຕີມຂໍ້ມູນທົດສອບ
            </Button>
          )}
        </div>
        <FormInput
          name="code"
          label="ລະຫັດ (VTE, NEAR, FAR)"
          placeholder="VTE"
          data-tourid="shipping-form-code"
        />
        <FormInput
          name="nameLo"
          label="ຊື່ (ລາວ)"
          placeholder="ພາສາລາວ"
          data-tourid="shipping-form-nameLo"
        />
        <FormInput name="nameTh" label="ຊື່ (ไทย)" placeholder="ภาษาไทย" />
        <FormInput name="nameEn" label="ຊື່ (EN)" placeholder="English" />
        <FormInput
          name="sortOrder"
          label="ລຳດັບ"
          type="number"
          min={0}
          data-tourid="shipping-form-sortOrder"
        />
        <div className="flex gap-2 pt-2" data-tourid="shipping-form-submit">
          <Button type="submit" disabled={submitting}>
            ບັນທຶກ
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={onCancel ?? (() => methods.reset())}
          >
            ຍົກເລີກ
          </Button>
        </div>
      </div>
    </FormRoot>
  );
}
